import { useState } from 'react'
import type { ClubInfo } from '../../types/captain'
import AppleMark from '../../components/AppleMark'

interface Props {
  club: ClubInfo
  /** Returns an error message string on failure, or null on success. */
  onSignIn: (email: string, password: string) => Promise<string | null>
  /**
   * Kicks off the Google OAuth redirect. Resolves with an error message string
   * only when the redirect could not be initiated; on success the page
   * navigates away before this resolves.
   */
  onGoogleSignIn: () => Promise<string | null>
  /** Same contract as `onGoogleSignIn`, for Sign in with Apple. */
  onAppleSignIn: () => Promise<string | null>
}

export default function AuthGate({ club, onSignIn, onGoogleSignIn, onAppleSignIn }: Props) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [googleSubmitting, setGoogleSubmitting] = useState(false)
  const [appleSubmitting, setAppleSubmitting] = useState(false)

  const busy = submitting || googleSubmitting || appleSubmitting

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setError(null)
    if (!email.trim() || !password) {
      setError('Enter your email and password to continue.')
      return
    }
    setSubmitting(true)
    const err = await onSignIn(email, password)
    setSubmitting(false)
    if (err) setError(err)
  }

  async function handleGoogleSignIn() {
    if (busy) return
    setError(null)
    setGoogleSubmitting(true)
    const err = await onGoogleSignIn()
    // On success the browser redirects away; we only reach here on failure.
    setGoogleSubmitting(false)
    if (err) setError(err)
  }

  async function handleAppleSignIn() {
    if (busy) return
    setError(null)
    setAppleSubmitting(true)
    const err = await onAppleSignIn()
    setAppleSubmitting(false)
    if (err) setError(err)
  }

  return (
    <div
      data-authgate=""
      style={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.2fr)',
        alignItems: 'stretch',
        position: 'relative',
      }}
    >
      {/* Floating wordmark anchor */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 40,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          zIndex: 5,
        }}
      >
        <img
          src="/full-logo.svg"
          alt="Stazon"
          width={73}
          height={22}
          style={{ width: 'auto', display: 'block', flexShrink: 0 }}
        />
        <span
          style={{
            width: 1,
            height: 14,
            background: 'rgba(244,242,242,0.18)',
          }}
          aria-hidden
        />
        <span
          style={{
            fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
            fontSize: 10,
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: 'rgba(198,196,185,0.55)',
          }}
        >
          Captain Recruitment
        </span>
      </div>
      {/* Left column — editorial copy + transit graphic */}
      <div
        data-authgate-left=""
        style={{
          padding: '120px 56px 80px 40px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: 60,
          borderRight: '0.5px solid rgba(244,242,242,0.06)',
          position: 'relative',
        }}
      >
        <div className="cp-cascade">
          <div
            style={{
              fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontSize: 11,
              color: '#C2E530',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              marginBottom: 36,
            }}
          >
            stazon.cc/captain/<span style={{ color: '#F4F2F2' }}>{club.slug}</span>
          </div>
          <h1
            style={{
              fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontWeight: 700,
              fontSize: 'clamp(36px, 5.2vw, 56px)',
              lineHeight: 0.98,
              letterSpacing: '-0.025em',
              color: '#F4F2F2',
              margin: 0,
              marginBottom: 22,
            }}
          >
            Run the
            <br />
            <span style={{ color: '#C2E530' }}>route</span>,{' '}
            <em
              style={{
                fontFamily: "'Lora', Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 400,
              }}
            >
              man the
            </em>
            <br />
            station.
          </h1>
          <p
            style={{
              fontFamily: "'Lora', Georgia, serif",
              fontSize: 16,
              lineHeight: 1.65,
              color: 'rgba(198,196,185,0.7)',
              maxWidth: 440,
              margin: 0,
            }}
          >
            You've been invited to volunteer as a captain for{' '}
            <em style={{ color: '#F4F2F2' }}>{club.name}</em>. Sign in with your Stazon account to
            see which upcoming events need pacers, sweepers, and leads.
          </p>
        </div>

        {/* Decorative transit line — a single vertical pictorial nod to the brand mark */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            color: 'rgba(198,196,185,0.4)',
            fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
            fontSize: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
          }}
        >
          <svg width="58" height="14" viewBox="0 0 58 14">
            <line x1="0" y1="7" x2="20" y2="7" stroke="rgba(244,242,242,0.18)" strokeWidth="1" />
            <ellipse cx="26" cy="7" rx="4" ry="5" fill="transparent" stroke="#C2E530" strokeWidth="1" />
            <line x1="26" y1="3" x2="26" y2="11" stroke="#C2E530" strokeWidth="1" strokeLinecap="round" />
            <line x1="32" y1="7" x2="58" y2="7" stroke="rgba(244,242,242,0.18)" strokeWidth="1" />
          </svg>
          You are here · {club.name}
        </div>
      </div>

      {/* Right column — sign-in form */}
      <div
        data-authgate-right=""
        style={{
          padding: '120px 40px 80px 56px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <form
          className="cp-cascade"
          style={{ width: '100%', maxWidth: 380 }}
          onSubmit={handleSubmit}
          noValidate
        >
          <div
            style={{
              fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              color: 'rgba(198,196,185,0.55)',
              marginBottom: 28,
            }}
          >
            Sign in to continue
          </div>

          <label style={labelStyle} htmlFor="cp-email">
            Email
          </label>
          <input
            id="cp-email"
            type="email"
            className="cp-input"
            placeholder="you@stazon.app"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ marginBottom: 24 }}
            autoComplete="email"
            disabled={busy}
          />

          <label style={labelStyle} htmlFor="cp-password">
            Password
          </label>
          <input
            id="cp-password"
            type="password"
            className="cp-input"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ marginBottom: error ? 18 : 36 }}
            autoComplete="current-password"
            disabled={busy}
          />

          {error && (
            <div
              role="alert"
              style={{
                marginBottom: 24,
                padding: '12px 14px',
                borderRadius: 6,
                background: 'rgba(239,96,106,0.08)',
                border: '0.5px solid rgba(239,96,106,0.4)',
                color: '#EF606A',
                fontFamily: "'Lora', Georgia, serif",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            className="cp-btn-primary"
            style={{ width: '100%', padding: '14px' }}
            disabled={busy}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
            {!submitting && (
              <span aria-hidden style={{ fontSize: 16, marginLeft: 2 }}>
                →
              </span>
            )}
          </button>

          {/* Divider */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              margin: '24px 0',
              color: 'rgba(198,196,185,0.4)',
              fontFamily:
                "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
            }}
          >
            <span style={{ flex: 1, height: 0.5, background: 'rgba(244,242,242,0.12)' }} />
            or
            <span style={{ flex: 1, height: 0.5, background: 'rgba(244,242,242,0.12)' }} />
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="cp-btn-google"
            style={{
              width: '100%',
              padding: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
            }}
            disabled={busy}
          >
            <GoogleMark />
            {googleSubmitting ? 'Redirecting…' : 'Continue with Google'}
          </button>

          <button
            type="button"
            onClick={handleAppleSignIn}
            className="cp-btn-apple"
            style={{
              width: '100%',
              padding: '14px',
              marginTop: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
            }}
            disabled={busy}
          >
            <AppleMark />
            {appleSubmitting ? 'Redirecting…' : 'Continue with Apple'}
          </button>

          <p
            style={{
              fontFamily: "'Lora', Georgia, serif",
              fontSize: 12,
              fontStyle: 'italic',
              color: 'rgba(198,196,185,0.45)',
              marginTop: 28,
              lineHeight: 1.6,
            }}
          >
            New here? Stazon is invite-only while we build out the captain experience — your club
            organizer will share an access link.
          </p>
        </form>
      </div>
    </div>
  )
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.14em',
  color: 'rgba(198,196,185,0.7)',
}
